import React from 'react'
import { cx, css } from '@emotion/css';
import Icon from '../../../../Icon2/Icon'
import InfiniteScrollTable from '../../../../Table/InfiniteScrollTable'
import Tooltip from '../../../../Tooltip/Tooltip';

import { formatTime, getAssetType } from '../../../commons/util';
import styles from './index.module.css'
import BranchDisplay from '../../../../BranchDisplay/BranchDisplay';

const GetIcon = ({ data }) => {
  const assetContentType = Array.isArray(data.content_type) ? data.content_type[0] : data.content_type
  let icon = getAssetType(assetContentType)
  const assetName = Array.isArray(data.name) ? data.name[0] : data.name
  const assetFileName = Array.isArray(data.filename) ? data.filename[0] : data.filename
  const assetUrl = Array.isArray(data.url) ? data.url[0] : data.url
  const title = assetName ? assetName : (assetFileName ? assetFileName : data.title);

  return (
    <React.Fragment>
      <span className={cx(styles['list-icon'], css`
        background: ${assetUrl ? `url(${assetUrl})` : 'inherit'};
      `)} 
      data-test-id="cs-list-view-title-icon">
        {icon && <Icon icon={icon} />}
      </span>
      <Tooltip position="top" content={title}>
        <div className={cx(styles['title-box'], "cs-asset-picker__title-box")} data-test-id="cs-list-view-title-box-text">
          {title}
        </div>
      </Tooltip>
    </React.Fragment>
  )
}

export const ListView = ({
  state,
  readOnly,
  selectedAssets,
  fetchOnChange,
  loadMore,
  handleClick,
  loading,
  users = {},
  multiple,
  assetKey,
  selectedAssetsMap,
  setSelectedAssetsMap,
  updateSelection,
  showUserDataColumn = true,
  hasBranchFeature,
  isFolderSelection = false
}: any) => {
  const isMultiple = multiple
  let selectedAssetsObj: any = {}
  selectedAssets?.forEach(asset => selectedAssetsObj[asset.uid] = true)

  // 'branch-display-dom' is UI-React dom element
  let appendToId = 'branch-display-dom'
  let branchDisplayDOM = document.getElementById('branch-display-dom')
  // Temporary element for the venus to render in Storybook
  // 'venus-branch-display-dom' is Storybook dom element
  if (!branchDisplayDOM) {
      appendToId = 'venus-branch-display-dom'
      branchDisplayDOM = document.createElement('div')
      branchDisplayDOM.id = appendToId
      document.body.appendChild(branchDisplayDOM)
      branchDisplayDOM = document.getElementById(appendToId)
  }

  const columns: any = [
    {
      Header: 'Name',
      id: 'name',
      accessor: (data) => {
        return (
          <div className="flex-v-center">
            {hasBranchFeature ? (
              <Tooltip
                content={
                  <BranchDisplay
                    moduleType={data.is_dir ? 'folder' : 'asset'}
                    moduleVal={data}
                    titleKey={data.is_dir ? 'name' : 'filename'}
                    vendor="asset_management"
                  />
                }
                appendTo={branchDisplayDOM}
                position="right"
                showArrow={false}
                variantType="light">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <GetIcon data={data} />
                </div>
              </Tooltip>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <GetIcon data={data} />
              </div>
            )}
          </div>
        )
      },
      default: false,
      addToColumnSelector: true
    },
    {
      Header: 'Unique Id',
      accessor: 'uid',
      id: 'uid',
      default: false,
      addToColumnSelector: true,
      disableSortBy: true
    }
  ]

  if (showUserDataColumn) {
    columns.push({
      Header: 'Last Modified By',
      id: 'updated_by',
      accessor: (data) => <div className={'last-updated-by'}><span>{users[data.updated_by] || 'Anonymous'}</span> <span className='last-updated-by-time'>{formatTime(data.updated_at)}</span></div>,
      default: false,
      addToColumnSelector: true,
      disableSortBy: true,
    })
  }

  const getSelectedRow = (_singleSelectedRowIds) => {
    let dataObj = state.assets.filter((obj: any) => {
      return _singleSelectedRowIds.includes(obj.uid)
    });
    let updatedObj = { ...selectedAssetsMap, [assetKey]: dataObj }
    setSelectedAssetsMap(updatedObj)
    let arr: any = []
    Object.values(updatedObj).forEach((ctArr: any) => {
      if (ctArr.length) {
        arr = [...arr, ...ctArr]
      }
    })
    updateSelection(arr)
  }

  const getOnlySelectedRows = () => {
    if (readOnly) {
      return (
        <InfiniteScrollTable
          tableHeight={290}
          data={selectedAssets}
          columns={columns}
          uniqueKey={'uid'}
          fetchTableData={() => { }}
          loadMoreItems={loadMore}
          loading={loading}
          totalCounts={selectedAssets.length}
          itemStatusMap={state.itemStatusMap}
          minBatchSizeToFetch={30}
          name={{ singular: 'Asset', plural: 'Assets' }}
        />
      )
    }
    else return null
  }

  return (
    <div style={{ height: '290px', overflow: "hidden" }} data-testid="list-view" className='scrte-infinite-list'>
      {!readOnly && (isMultiple ?
        <InfiniteScrollTable
          rowSelectCheckboxProp={{ key: 'is_dir', value: isFolderSelection }}
          tableHeight={290}
          data={state.assets}
          columns={columns}
          uniqueKey={'uid'}
          fetchTableData={fetchOnChange}
          loading={loading}
          totalCounts={state.count}
          loadMoreItems={loadMore}
          itemStatusMap={state.itemStatusMap}
          minBatchSizeToFetch={30}
          initialSortBy={[{ id: 'name', desc: true }]}
          columnSelector={false}
          fullRowSelect={true}
          initialSelectedRowIds={selectedAssetsObj}
          isRowSelect={true}
          onRowSelectProp={[]}
          getSelectedRow={getSelectedRow}
          maxSelect={typeof multiple !== 'boolean' && multiple?.max}
          name={{ singular: 'Asset', plural: 'Assets' }}
          onRowHoverText={(data: any) => data.errorMessage}
          rowDisableProp={{ key: 'invalid_asset', value: true }}
          onRowClick={handleClick}
        />
        : <InfiniteScrollTable
          tableHeight={290}
          data={state.assets}
          columns={columns}
          uniqueKey={'uid'}
          fetchTableData={fetchOnChange}
          loadMoreItems={loadMore}
          loading={loading}
          totalCounts={state.count}
          onRowClick={handleClick}
          singleSelectedRowId={selectedAssets?.length && selectedAssets[0].uid}
          itemStatusMap={state.itemStatusMap}
          columnSelector={false}
          minBatchSizeToFetch={30}
          name={{ singular: 'Asset', plural: 'Assets' }}
          rowDisableProp={{ key: 'invalid_asset', value: true }}
        />
      )}
      {getOnlySelectedRows()}
    </div>
  )
}